import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  ADD_PAYMENT_METHOD_KEY,
  ADYEN_TYPE_TO_PAYMENT_METHOD_TYPE_MAP,
  AdyenDropinOnSubmitData,
} from '@/utils/payments';
import {
  AdyenConfigType,
  PAYMENT_CONTEXT_QUERY,
  usePaymentsContext,
} from '@/contexts/payments-context';
import { AdyenDropinWeb } from './adyen-dropin-web';
import { AdyenDropinWebView } from './adyen-dropin-webview';
import {
  AlertLevel,
  CardFundingSource,
  Payments_GetAdyenDropinConfigQuery,
  Payments_GetAdyenDropinConfigQueryVariables,
  Payments_SavePaymentMethodMutation,
  Payments_SavePaymentMethodMutationVariables,
} from '@/types';
import { Button } from '@/components/button';
import {
  formatCurrency,
  getCountryCurrencyCode,
  MICRO_CONVERSION,
} from '@/utils/money';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation, useQuery } from '@apollo/client';
import { PaymentMethodType } from '@/enums';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { toFixedDecimalPercentage } from '@/utils/percentage';
import { TransactionFeeMap } from '@/utils/transaction-fees';
import { useGetCardDetails } from '@/hooks/payments/use-get-card-details';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';
import { WebView } from 'react-native-webview';
import type { Dropin } from '@adyen/adyen-web';

interface Props {
  onAddUnsavedMethod?: (json: string) => void;
  onSaveSuccess: () => void;
  requireSavedPaymentMethod?: boolean;
  transactionFeeMap: TransactionFeeMap;
}

export const GET_ADYEN_DROPIN_CONFIG_QUERY = gql`
  query Payments_GetAdyenDropinConfig(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getAdyenDropinConfig(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      client_key
      environment
      payment_methods_json
    }
  }
`;

export const SAVE_PAYMENT_METHOD_MUTATION = gql`
  mutation Payments_SavePaymentMethod(
    $paymentMethodString: String!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    savePaymentMethod(
      payment_method: $paymentMethodString
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      alert_level
      alert_message
      payment_method_id
    }
  }
`;

const TRANSACTION_FEE_DISPLAY_ORDER = [
  PaymentMethodType.DEBIT_CARD,
  PaymentMethodType.CREDIT_CARD,
  PaymentMethodType.ACH,
  PaymentMethodType.PAYTO,
];

export const AdyenDropinWrapper: React.FC<Props> = ({
  onAddUnsavedMethod,
  onSaveSuccess,
  requireSavedPaymentMethod = false,
  transactionFeeMap,
}) => {
  const { bottom } = useSafeAreaInsets();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();
  const {
    countryCode,
    enabledPaymentMethods,
    loading: paymentsContextLoading,
  } = usePaymentsContext();
  const { showErrorToast } = useToasts();
  const getCardDetails = useGetCardDetails();

  const { data: adyenData, loading: adyenConfigLoading } = useQuery<
    Payments_GetAdyenDropinConfigQuery,
    Payments_GetAdyenDropinConfigQueryVariables
  >(GET_ADYEN_DROPIN_CONFIG_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      restaurantUUID,
      supplierUUID,
    },
  });

  const config = useMemo<AdyenConfigType | null>(() => {
    if (!adyenData?.getAdyenDropinConfig || !countryCode) {
      return null;
    }
    const { client_key, environment, payment_methods_json } =
      adyenData.getAdyenDropinConfig;
    return {
      clientKey: client_key,
      countryCode,
      environment,
      paymentMethodsJson: payment_methods_json,
    };
  }, [adyenData?.getAdyenDropinConfig, countryCode]);

  const [canContinue, setCanContinue] = useState(false);

  const webViewRef = useRef<WebView>(null);
  const webDropinRef = useRef<Dropin>(null);

  const [savePaymentMethod, { loading }] = useMutation<
    Payments_SavePaymentMethodMutation,
    Payments_SavePaymentMethodMutationVariables
  >(SAVE_PAYMENT_METHOD_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [getOperationName(PAYMENT_CONTEXT_QUERY) as string],
  });

  const feeDisplayString = useMemo(() => {
    const hasTransactionFees = Object.values(transactionFeeMap).some(
      // Checks if individual fee values are truthy
      // Fee values can be set to null due to Pydantic
      (feeMap) => Object.values(feeMap).filter((x) => x).length,
    );

    return hasTransactionFees
      ? TRANSACTION_FEE_DISPLAY_ORDER.reduce<string[]>((acc, type) => {
          // If type is not enabled, don't show the fee
          if (!enabledPaymentMethods.includes(type)) {
            return acc;
          }
          let transactionFeeDisplayAmount = '';
          const flatTransactionFee = transactionFeeMap.FLAT[type];
          const percentageTransactionFee = transactionFeeMap.PERCENTAGE[type];

          if (flatTransactionFee) {
            transactionFeeDisplayAmount = formatCurrency(
              flatTransactionFee * MICRO_CONVERSION,
              getCountryCurrencyCode(countryCode ?? ''),
            );
          } else if (percentageTransactionFee) {
            transactionFeeDisplayAmount = toFixedDecimalPercentage(
              percentageTransactionFee,
            );
          }

          if (transactionFeeDisplayAmount) {
            acc.push(
              i18n.t(`transaction_fee.${type.toLowerCase()}`, {
                amount: transactionFeeDisplayAmount,
              }),
            );
          } else {
            acc.push(i18n.t(`transaction_fee.no_${type.toLowerCase()}`));
          }

          return acc;
        }, []).join('; ')
      : '';
  }, [countryCode, enabledPaymentMethods, transactionFeeMap]);

  const callSavePaymentMethod = useCallback(
    async (paymentMethodString: string) => {
      try {
        const response = await savePaymentMethod({
          variables: {
            paymentMethodString,
            restaurantUUID,
            supplierUUID,
          },
        });

        const result = response.data?.savePaymentMethod;
        if (result?.alert_level === AlertLevel.Negative) {
          showErrorToast(result.alert_message!);
        } else if (result?.alert_level === AlertLevel.Positive) {
          onSaveSuccess();
        }
      } catch (e) {
        showErrorToast();
      }
    },
    [
      onSaveSuccess,
      restaurantUUID,
      savePaymentMethod,
      showErrorToast,
      supplierUUID,
    ],
  );

  const onSubmitDropin = useCallback(
    async (adyenData: AdyenDropinOnSubmitData) => {
      const paymentMethodType =
        ADYEN_TYPE_TO_PAYMENT_METHOD_TYPE_MAP[
          adyenData.data.paymentMethod.type
        ];

      // If the payment method being used is a credit card, and the user is not saving the payment method,
      // there is a chance that the card is a visa debit card, so we need to check the card's
      // funding source to set the correct fees. Otherwise, we call the save payment method with
      // adyen's data, and the server will handle the card type.
      if (
        !requireSavedPaymentMethod &&
        paymentMethodType === PaymentMethodType.CREDIT_CARD &&
        !adyenData.data.storePaymentMethod
      ) {
        if (adyenData.data.paymentMethod.encryptedCardNumber) {
          const { data } = await getCardDetails({
            variables: {
              encryptedCardNumber:
                adyenData.data.paymentMethod.encryptedCardNumber,
            },
          });
          if (data?.getCardDetails.funding_source === CardFundingSource.Debit) {
            adyenData.data.paymentMethod.type = 'debit';
          }
        }
        onAddUnsavedMethod?.(JSON.stringify(adyenData));
      } else {
        callSavePaymentMethod(JSON.stringify(adyenData));
      }
    },
    [
      requireSavedPaymentMethod,
      getCardDetails,
      onAddUnsavedMethod,
      callSavePaymentMethod,
    ],
  );

  const onChangeDropin = useCallback(() => {
    // We don't care what changed about the drop in, but the indication
    // that *any* change happened means that the user has interacted with
    // the drop in and we can now enable the "Continue" button
    setCanContinue(true);
  }, []);

  const renderWebView = Platform.OS !== 'web';

  // ensure payments context query has the real countryCode  before rendering
  const isLoading = adyenConfigLoading || paymentsContextLoading;

  // Surface the failure once both queries have settled without a usable
  // config. Kept in an effect so render stays pure — otherwise the toast
  // re-fires on every re-render while config is null.
  useEffect(() => {
    if (!isLoading && !config) {
      showErrorToast();
    }
  }, [isLoading, config, showErrorToast]);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator />
      </View>
    );
  }

  if (!config) {
    return null;
  }

  return (
    <View
      style={[styles.base, { marginBottom: Math.max(bottom, SCREEN_PADDING) }]}
    >
      {renderWebView ? (
        <AdyenDropinWebView
          config={config}
          onChange={onChangeDropin}
          onSubmit={onSubmitDropin}
          requireSavedPaymentMethod={requireSavedPaymentMethod}
          webViewRef={webViewRef}
        />
      ) : (
        <AdyenDropinWeb
          config={config}
          onChange={onChangeDropin}
          onSubmit={onSubmitDropin}
          requireSavedPaymentMethod={requireSavedPaymentMethod}
          webDropinRef={webDropinRef}
        />
      )}
      {feeDisplayString ? (
        <Text style={[TYPOGRAPHY.xsmall, styles.transactionFeeText]}>
          {feeDisplayString}
        </Text>
      ) : null}
      <Button
        disabled={!canContinue}
        loading={loading}
        onPress={() => {
          if (renderWebView && webViewRef.current) {
            webViewRef.current.postMessage(ADD_PAYMENT_METHOD_KEY);
          } else if (!renderWebView && webDropinRef.current) {
            webDropinRef.current.submit();
          }
        }}
        style={styles.button}
        testID="add-payment-method-button"
        trackingData={{
          name: 'payments.add_payment_method_button',
          properties: {
            restaurantUUID,
          },
        }}
      >
        {i18n.t('continue')}
      </Button>
    </View>
  );
};

const styles = StyleSheet.create({
  base: {
    flex: 1,
  },
  button: {
    marginHorizontal: SCREEN_PADDING,
    marginTop: 12,
  },
  loadingContainer: {
    alignItems: 'center',
    paddingBottom: 48,
    paddingTop: 32,
  },
  transactionFeeText: {
    alignSelf: 'center',
    marginTop: 12,
  },
});
